/**
 * Access-restriction presentation contract.
 *
 * The backend is the sole authority on whether a tenant may use the
 * application: `TenantActiveGuard` and `SubscriptionGuard` refuse every
 * protected request with a 403 regardless of what this module decides. All this
 * file does is translate the guard's already-final answer into the copy for one
 * screen.
 *
 * Consequently:
 *  - Nothing here grants access. Removing this file would make the app uglier,
 *    not less secure.
 *  - The reason is read from the `error.code` the server sent, never from the
 *    message text and never as a security decision.
 *  - An unrecognised code degrades to `generic`, which says the least.
 */

export const ACCESS_RESTRICTION_REASONS = ['suspended', 'expired', 'cancelled', 'generic'] as const;

export type AccessRestrictionReason = (typeof ACCESS_RESTRICTION_REASONS)[number];

/**
 * Server error codes that mean "authenticated, but this workspace may not be
 * used". Kept in lockstep with
 * `backend/src/subscription/constants/subscription-access.codes.ts`.
 */
const CODE_TO_REASON: Record<string, AccessRestrictionReason> = {
  SUBSCRIPTION_SUSPENDED: 'suspended',
  SUBSCRIPTION_EXPIRED: 'expired',
  SUBSCRIPTION_CANCELLED: 'cancelled',
  SUBSCRIPTION_INACTIVE: 'generic',
  TENANT_ACCESS_RESTRICTED: 'generic',
};

/** The reason for a known restriction code, or `null` if this is an ordinary 403. */
export function accessRestrictionReasonForCode(
  code: string | undefined,
): AccessRestrictionReason | null {
  if (code === undefined) return null;
  return CODE_TO_REASON[code] ?? null;
}

/**
 * Validates a `?reason=` search param. Presentation-only fallback for a page
 * refresh, which empties the in-memory store — see AccessRestrictedPage. A
 * fabricated or absent value resolves to `generic`; it cannot resolve to
 * "allowed", because this function has no such return value and the backend
 * would refuse the request anyway.
 */
export function parseAccessRestrictionReason(value: string | null): AccessRestrictionReason {
  const candidate = (value ?? '').toLowerCase();
  return (ACCESS_RESTRICTION_REASONS as readonly string[]).includes(candidate)
    ? (candidate as AccessRestrictionReason)
    : 'generic';
}

export interface AccessRestrictionCopy {
  /** Page <h1>. */
  title: string;
  /** What has happened. */
  message: string;
  /** What to do about it. */
  secondary: string;
}

export const ACCESS_RESTRICTION_COPY: Record<AccessRestrictionReason, AccessRestrictionCopy> = {
  suspended: {
    title: 'Access temporarily suspended',
    message: "Your organization's Service Portal access is currently suspended.",
    secondary: 'Please contact your service provider or customer care for assistance.',
  },
  expired: {
    title: 'Subscription expired',
    message: "Your organization's Service Portal subscription has expired.",
    secondary: 'Please contact your service provider or customer care to restore access.',
  },
  cancelled: {
    title: 'Service access ended',
    message: "Your organization's Service Portal subscription is no longer active.",
    secondary: 'Please contact your service provider or customer care if you need assistance.',
  },
  generic: {
    title: 'Access unavailable',
    message: "We're unable to provide access to your organization's Service Portal right now.",
    secondary: 'Please contact your service provider or customer care for assistance.',
  },
};
